import React from "react";
import { useParams } from "react-router-dom";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import DashboardPage from "@/pages/DashboardPage";
import UsersPage from "@/pages/UsersPage";
import RolesPage from "@/pages/RolesPage";
import AuditPage from "@/pages/AuditPage";
import MetodosPagoPage from "@/pages/sistema/MetodosPagoPage";
import MotorCalculoPage from "@/pages/motor/MotorCalculoPage";
import ProveedoresPage from "@/pages/maestros/ProveedoresPage";
import TripsPage from "@/pages/trips/TripsPage";
import CotizadorPage from "@/pages/cotizador/CotizadorPage";
import CuratedPage from "@/pages/curated/CuratedPage";
import PlantillasPage from "@/pages/plantillas/PlantillasPage";
import ClientesPage from "@/pages/clientes/ClientesPage";
import TesoreriaPage from "@/pages/finanzas/TesoreriaPage";
import GastosFijosPage from "@/pages/finanzas/GastosFijosPage";
import ContabilidadPage from "@/pages/finanzas/ContabilidadPage";
import MigracionPage from "@/pages/migracion/MigracionPage";
import Sofi1SyncPage from "@/pages/sistema/Sofi1SyncPage";
import TravefySyncPage from "@/pages/sistema/TravefySyncPage";
import AutomatizacionesPage from "@/pages/sistema/AutomatizacionesPage";
import KimKimBackfillPage from "@/pages/sistema/KimKimBackfillPage";
import ImportsAdminPage from "@/pages/sistema/ImportsAdminPage";
import LeadsPage from "@/pages/leads/LeadsPage";
import FacturasPage from "@/pages/facturas/FacturasPage";
import PipelinePage from "@/pages/pipeline/PipelinePage";
import ZicassoDashboard from "@/pages/zicasso/ZicassoDashboard";
import ChatPage from "@/pages/chat/ChatPage";
import ClientChatPage from "@/pages/client_chat/ClientChatPage";
import ReconfirmacionesPage from "@/pages/reconfirmaciones/ReconfirmacionesPage";
import FareHarborWebhooksPage from "@/pages/asistente/FareHarborWebhooksPage";
import LaboralPage from "@/pages/laboral/LaboralPage";
import GuardiasPage from "@/pages/guardias/GuardiasPage";
import RRHHPage from "@/pages/rrhh/RRHHPage";
import RegistroHorarioPage from "@/pages/rrhh/RegistroHorarioPage";
import MiAgendaPage from "@/pages/agenda/MiAgendaPage";
import InventarioPage from "@/pages/inventario/InventarioPage";
import AyudaPage from "@/pages/ayuda/AyudaPage";
import FormacionPage from "@/pages/formacion/FormacionPage";
import BiPage from "@/pages/bi/BiPage";
import InformeVentasPage from "@/pages/bi/InformeVentasPage";
import InformesAutoPage from "@/pages/InformesAutoPage";
import ModulePlaceholderPage from "@/pages/ModulePlaceholderPage";
import LaboralPinGate from "@/pages/laboral/LaboralPinGate";

const AccessDenied = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <Card className="max-w-md text-center" data-testid="access-denied">
      <CardContent className="space-y-3 p-10">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
          <ShieldX className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-bold">Sin acceso</h2>
        <p className="text-sm text-muted-foreground">No tienes permiso para ver este módulo.</p>
      </CardContent>
    </Card>
  </div>
);

const REAL_PAGES = {
  dashboard: DashboardPage,
  usuarios: UsersPage,
  roles_perms: RolesPage,
  auditoria: AuditPage,
  metodos_pago: MetodosPagoPage,
  motor_calculo: MotorCalculoPage,
  proveedores: ProveedoresPage,
  trips: TripsPage,
  cotizador: CotizadorPage,
  curated: CuratedPage,
  plantillas: PlantillasPage,
  clientes: ClientesPage,
  cashflow: TesoreriaPage,
  gastos_fijos: GastosFijosPage,
  contabilidad: ContabilidadPage,
  migracion: MigracionPage,
  sofi1_sync: Sofi1SyncPage,
  travefy_sync: TravefySyncPage,
  kimkim_backfill: KimKimBackfillPage,
  imports_legacy: ImportsAdminPage,
  leads: LeadsPage,
  facturas: FacturasPage,
  pipeline: PipelinePage,
  zicasso: ZicassoDashboard,
  chat: ChatPage,
  chat_clientes: ClientChatPage,
  chat_support: ClientChatPage,
  reconfirmaciones: ReconfirmacionesPage,
  webhooks_fh: FareHarborWebhooksPage,
  laboral_fichas: LaboralPage,
  laboral_nominas: LaboralPage,
  laboral_evolucion: LaboralPage,
  laboral_prenomina: LaboralPage,
  guardias: GuardiasPage,
  rrhh: RRHHPage,
  registro_horario: RegistroHorarioPage,
  mi_agenda: MiAgendaPage,
  inventario: InventarioPage,
  ayuda: AyudaPage,
  formacion: FormacionPage,
  bi: BiPage,
  informe_ventas: InformeVentasPage,
  informes_auto: InformesAutoPage,
  automatizaciones: AutomatizacionesPage,
};

// Módulos protegidos por el PIN Laboral (grupo LABORAL en rbac.py).
// Nota: `laboral_*` ya llevan el gate embebido dentro de LaboralPage.
// Aquí añadimos módulos "standalone" del grupo LABORAL que no lo tienen.
const LABORAL_STANDALONE_MODULES = new Set(["inventario"]);

export default function ModuleRouter() {
  const { moduleId } = useParams();
  const { modules, navigation } = useAuth();

  if (!modules.includes(moduleId)) return <AccessDenied />;

  const Page = REAL_PAGES[moduleId];
  if (Page) {
    if (LABORAL_STANDALONE_MODULES.has(moduleId)) {
      return (
        <LaboralPinGate>
          <Page moduleId={moduleId} />
        </LaboralPinGate>
      );
    }
    return <Page moduleId={moduleId} />;
  }

  const meta = navigation.flatMap((g) => g.items).find((it) => it.id === moduleId);
  return <ModulePlaceholderPage meta={meta} />;
}
